import {
  assertEvent,
  assign,
  fromPromise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({input}: {input: {eventId: string; reason: string}}) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return context.eventDetails?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellation: ({context}) => context.currentRoute === 'cancel',
    isReschedule: ({context}) => context.currentRoute === 'reschedule',
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!context?.eventDetails?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHost: ({context}) => context.owner === 'HOST',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAbAHYALOSkBOBRLEKpADgDMAJnUSANCACeiALSbZ8gKyaFtqxplMpEqwF83htJnKwMeZGxmNiQQTh4+ASFRBDFtTXImdRk9KwkJdSYZVSlDEwRtBStyGSsFGR0KlysmbQ8vdAxff0CsBjEQji5efkFQmOl1cjEHTWkZJ20RhTzEbRdyGxspbSZNTTEkyXqQbya+AFswAC8BMAAZVDwISDbWIXCeqP7xGSlhrIsp5PVN7StZggJDIJOR1AptBJnFI4lo0js9uRDiczpdrrd2p0wt1In1QDFysUQckmMDfhUrNpAcDQeD-lJNFZfqS6p5do0kdwjqcCBcrjcIG1tFjHrjonNygkmExCSsvlYxNSVIkkhNXIosnoERzkTywAAFbgAYwA1mBkFhdWdfGAMMEHjjeuKEG8mCULEztDIvRV1PFAdltPJfbV1FZvc5tmzEWAAG5gAgYNEQbhELAQa2p2OoM3kGPxxPJ1NQBBZ1BGui9YL20Kip0vF26eRQiSFSm6NRaQGmbTxbU+OMJpP84tYc1oZDkKj4DAAM1QyAOeY5g8LI6IpYI2YruOr91rjue+MQ3qGLhZRV74I0mm78zE-aasDgPAERbTGd55DLucRz9gr4EO+JZljuVasDWXQRPWx4IJewyVAo6iyGUtQzMY4jSkMChiFINROCoOg2I+NoAb0wFjsgE5TjO86LsuPj-oBwGbtulYCHuIqHniIjiJCCjkPEUKkuCTBiIGgK-Akei4SCTIoX6JGrhgAAqYAHNOdB8tco6fmA35bjm+n5kOakaTO2kphuoHsQQnEOtBR68QUChMMUyRWOGGS6JomQAhhsRue8KiQmI4k2Os6hKQWqnqZp9AUeOC40XQdFLiZiZmfFlnFqx5a2fZB6OTxMQ6NI5DAg4YYTBsyGSeJ5A4ZIaTSDhUiKCRNz0IuqZgAA8gA7ryFp6QZ2a-hyXXmgcvWDcNeVgRxEH7lBTwlSeGzyGIHrMs4tRjN2GwKNFQ7AQAolRC5YMgtrIEYkHYsVzrZAJKi-NteEVLhuQBRkCSrLUIwaOJziaCRTHkfyGIrY9a3OnYQZ0oomjeusXo-fk21xMMIxQi1cTZO40YchDb5Q4KmIOXDDaqKsJTIesbx6Izt4BXE8wlNIXqbM4yErODL69BdE7Xbd90w3WTkxHESGNSCyiKOJ3p2ICKxDJ5EhKMsroZCRABG6AmsWABiOCoAN6aZoZuYGzmJtmwND2S+tBThgk0xhZrMKaDKgJK6C8zlLIXpwqU+uG-b5uW1+P76bbRtEKb5uU0V1OwRsTY2G5FgMrovas5j8uCWktRvRoeGsg0A4xVlFnC1dN0YHdTvcc66xjIsYxSJqEIsv5+SawJFhZDCCpevEUXEz45vDcm0NcU9NOlM2pJhZIjLKOJfuDJ3naSO1pLrCRM-mnPFMdFTYo0zoAmlIRvz-J5azb8kiSlAyKhpP8EjH0Np-k0KBeadnKrGxiCV0jgViaypGzHeNglCyDEr5eS4c7ZEAAMrECNEaF80d9KxwYk0eOxZMHYJfAtAqy0gFX1gv8ZC1gkhCUvGsfu4hPqJBUGFcEaQHDAhIhWAgOCcAzl6EnAaBleB3GoTBZy5hWz0xwq5DeyEliAnzm6BUCpNbs1bBCfhhAhEiIEGIiRQQL6pxoc5cqWhNjqlwl-HQaj4gaJGIUFYnkigSDBlPJoAjDG2RMamSRDBhSXxkTEZI7xwRfRSMPdYGM5jOMWK4tQj9PHeKrr4gxYBhEBIdnmMAeBYDGIXEuNKosiklPorAYgesZp2glq3BsvYwTAkZG2con9khqOBAJUkZR5guFsNIfRgiclGIICYm6lTJmlN8LU+pfAPxW3GsZDkfjxl5PNgUmZxs5k1LqbwJZIFDKLTslQsJUszDZHICjGoqoag1GQnoQEKMGSd3WEUOw1VnCjP8biKZhTimzOqQso5o4kqTnimlQh5ANm5IBfk6ZwK9mgsORgY5FDdwXIseEuYKxBJQm2soNY0pGRqKkmCbuEw7CqHSF6P5mzEXbORVUpckKKkov2WChp0irkFGVEoUoblH5uRkEUNRMIBI+Q8noLx4qSI3VgEaAAFpAYgOAwAmJwFyXgAA5TAAAlQpqr56XJdttOmbwHC6HavECYMD8iP2wgqPC4lMi8ykIquApqIAaq1fknV9SDUYGNXgX1bRzGrUsdLLxQxCgWHEgfGoYh1BqKWI1PCawvGtkkJXdkPglW+v9dq3VGAQ1hojSEvlLtViFFuTKMSQVXBhnTXhW5vwoHpAsHYGQ3rlVqr9ZqwFuy5nlNZSCpcBz6kt0XrBGWtJIk1CUFCTIBgAoJrdH6X4Qq1gwgfD4gpA71XDqRUCtl8z0XHLwWNIysKi2DpLWe0daLFm5Rstilgs7gH5B9hoEoSRfK53iN7Qwsb3nhi1u6zIYYMkFqaA+k9AaWXnsnZet9aZIUpTnHMxEiGh3IfERO1FU6eWYo-eBL9jS50BT-UMJwWgGb51A85feCRINFGg0kYih78NPpQy+9ll0LTEe5ei79MbxAPzBAqUohRpR43Xb+zYYhGrcc2Ckdqvl+3FtPds4hicHY3oIQZqAYiJN4tiArCqrYgqdKcL5NRzggzxEZJ6baNhJ6ZKPbpwj5BTNiOM9bOOEdDPJyjbDSTCBIpbtkKusYWhWxqLXgokE8RFH-D7bxn1j69PiKCUEajwDpaQnVi4EYVVXBqBkGojY-00YOrEmFODeGctIcCQQYJEXnbOm2jKQSJKNC1HDA4WrahFjIXErYaU21UEJygAAYWyTgEjWBTNLbGStsTM6itRY7EGCEvlexKCTV4pxMJgw3hEhCLQc3iwbaEZgq9ukVl3sROt5bT2MMnLYp+iz-LChODBAgpt3oNDoSdcoUEtQ74o3aXYO7RAHs5K++CzDwnsMwve6Fxbn2yPvtOZQqjNa262uGE4XCag+tjCU3McVQ9U0MjajNsMiPcebfrhaD7m2SPod4P9l2Og9AVRGFJcVYZPK1YqIkP0PdKSeZ-oeug9ACA3DAAAcQTOaLSgo+OavIFAVAAvnT-EZBVbuhRcLTAqH7IOJRxKuE2GMVNXiSLK4TGrzXw0ddYHhRMg3RvduWZ9hzZGPtmquBp7b9Y5vJCHxuyCDwbICCoBuPAUIexzXOlMKUej4JVAsKQnhclAUc+khKEoJCDg3juMV95vwAQMBZ5pjheQIOUiSi8bhP2fpFjA17EkZQGwsveatLyM+zf50pBKB4lGeg1awYDGblYnw-Q1EUJ9EiY+DTGjNMgSfVj3kalWN3WxYUAwc3X9kMYIJcJuROmuHSRAD-S29PIEk0hagdg0JJLI8g4fzA-BFCs6HqkxATrhQAv6IDfIi4ZCthaLgK-5yAMgVCAEd5MhEzebKS1xaTARQEuTSiNRqCeQprShJCSQtKybQaKA2CtidS2jTSzR-z764r8rEpyxSQbDSgW4HRsxJqCTSiXg5wdgP7DhP5QCc74Hyrk4iqDLqDJDSDUgQiNRhSuReJ3LBwCxkRkzogQD4GeIVRuRYxMi7ppp8FvACGNrZBwjiRaGASSGsEWrhiqbipxrdpehiSsIICyCqalxaZTauEHreYBYOz4GW5yAyh9bipuZvB+whwDZcyTbg4j7wbkDYFxR1zCb4FjDxAIRMhGHwHqiqyprujfShheiQa-yzzkz4G4wVQVARjzA8LKBeEO5BhMh5rHbbQZBBGpFgFnTCBUDcA3R6GOHwy4Qqi6D5HJBNEJKxApByA2JtJexaKYGpGmakI4IARhHV6CTw5KC9jpA1awI+xvzxC6AequT5qIgp58CzhGCbEvjZFvBBjlwgbIRqBqF+z7pggjCMJFCqjtSMoIqiKhFjENg56bAKIF7KLF5eGrAWFuoMieyQYjKHp+5bL5adZN7gmwQ9i-C3L-DiSuYoxV7wkebFyM6Qh4SKxeronLYTImIYm4j6ioA6pGj5DRqWbmA1C3LAjfDSBxDDK06uz0KeTCoQgpBejHT0mbaMnPpcqLj4GmBQi-Frw2JvC+RuRqJFzeiuAlzRErrAnykCaKllKlL4H-pTADLgLpCtiKEboayNSQgrFlCaxvDGmYk7Jml84YrFjKnYzvRcLD4Mh+QFz4rKF1bPJHb2p16pHMmgmmkXqQr4HhjvCug4SeS+SZlzH3jSpaCbxhRYR0Gyn-KJn5Y3CJjcCzjGiYmWlFAfDdEAlhSxEbpvCqbF4pBYwRh6LZbHoEZiK1GqDuTQheJIQwY2DJZQndoqBQgLFxA6a5Z+ZBr6pGomqDq1E+xuhCETBvBVYl5Oo16LCtjtyShNotYch65+aiZKm4msZejSSjm2DyHcbhkFDlzyCRhyQ0hpAXmFptYDkKkXppSbmUh7Fd4jCqCpoUrton5JBrCZmQiLntZAVobTpo6QF3klat6ZDtRjkvmL60a8wAaMbAaJp-kIYAX8ZEaoa84plYXiBD6JCSn7Spo+zhhqLKByCUgTAW4qZlBrGtb9nUX+Y46DkMWxBukVRaDsWuR6CFBmFOoZBugyg1Ah6O6th0neZXmBKVl3G1nMoDRhE+xDBjmQgmFISSDppMitJTASkOojDIWAXbIFabkgicxOCuTKBKIZDJayzrzqF+RkHXGXlUV5Y+ZLnnBlqVobkSXNRujAhuquHtRD61aSCJCKB3yhieFxl4awCxhUDiVclsEnbyCQilCSCaYVZ+y7nFx4weH7QdSHrc5CIkZhEQhBhZC35lCwi9hObLyeRJoqKRE6Bs7I44Co5+nP4SXyYJXeibDtSlBjBqA6mqkyiVSJXpDbrAmPHbEzV2CxaR7OB2DfLHG-opC0i8zOAUhxDwjNU47jUOHFWC6thBjgLOC9IKZ1Qbpw4y4IncKm5aiHrGoACOxAcAoaYVYAu16ez1vWj8ZxoYYYbFLgry+RNmlxNqdpglHI7uquYAGuWuyAOuOx42DIlINImQOg6wPe3c7+4qTgZQzyagSebgQAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'eventLoading',
          guard: {
            type: 'isEditBooking',
          },
        },
        {
          target: 'sessionLoading',
          guard: {
            type: 'isSession',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if (context.eventDetails?.id) {
            return {
              eventId: context?.eventDetails?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      invoke: {
        id: 'bookingFlow',
        input: ({context: {timezone, eventTemplate, language, session}}) => {
          if (timezone) {
            return {
              timezone,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellation',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isReschedule',
          },
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
      ],
      entry: assign({
        language: ({context}) => {
          const users =
            context.owner === 'HOST'
              ? context?.eventDetails?.interviewers
              : context?.eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: eventDetails?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },
    notifySuccess: {
      type: 'final',
    },
    cancellationFlow: {
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  if (context.eventDetails?.id) {
                    return {
                      eventId: context.eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        context.eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context: {eventDetails}}) => {
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHost',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  if (context.eventDetails?.id) {
                    return {
                      eventId: context.eventDetails?.id,
                      reason: event.reason,
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          invoke: {
            id: 'bookingFlow',
            input: ({
              context: {timezone, eventDetails, attendee, language},
            }) => {
              if (timezone) {
                return {
                  timezone,
                  eventDetails,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context: {eventDetails}}) => {
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {eventDetails, aid} = context;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          assertEvent(event, 'bookingCancelForm.submit');
          if (context.eventDetails?.id) {
            return {
              eventId: context.eventDetails?.id,
              reason: event.reason,
              viewerTimezone:
                context.eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
